<script setup lang="ts">
import {computed, nextTick, onMounted, reactive, ref, watch} from 'vue'
import {Check, CircleHelp, Flag, Search, Square} from 'lucide-vue-next'
import {useTranslationsStore} from '@/stores/translations'
import {cpApi} from '@/api'
import TranslationMaterial from '@/components/TranslationMaterial.vue'
import TranslationSettings from '@/components/TranslationSettings.vue'
import {Accordion, AccordionContent, AccordionItem, AccordionTrigger} from '@/components/ui/accordion'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Progress} from '@/components/ui/progress'
import {RadioGroup, RadioGroupItem} from '@/components/ui/radio-group'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs'
import type {SearchResultItem} from '@shared/types'

const translationsStore = useTranslationsStore()
translationsStore.initFromLocalStore()

const selectedMenuKeys = ref<string[]>([])
const menuRef = ref<HTMLElement | null>(null)
const activeTab = ref('navigation')
const customInstructionsOpen = ref<string[]>([])
const searchParams = reactive({text: '', scope: 'both' as 'material' | 'translate' | 'both', skip: 0, limit: 20})
const searchResults = ref<SearchResultItem[]>([])
const totalSearchResults = ref(0)
const isSearching = ref(false)
const currentPage = ref(1)
const totalPages = computed(() => Math.max(1, Math.ceil(totalSearchResults.value / searchParams.limit)))
const SELECT_NONE = 'none'
type TranslationStatus = 'check' | 'ok' | 'sample'

function setTargetLang(value: unknown): void {
  if (typeof value === 'string') translationsStore.targetLang = value
}

function setNativeLang(value: unknown): void {
  if (typeof value === 'string') translationsStore.nativeLang = value
}

function setLevel(value: unknown): void {
  if (typeof value !== 'string' || value === SELECT_NONE) return
  translationsStore.level = Number(value)
}

function setUnit(value: unknown): void {
  if (typeof value !== 'string' || value === SELECT_NONE) return
  translationsStore.unit = Number(value)
}

function setLesson(value: unknown): void {
  if (typeof value !== 'string' || value === SELECT_NONE) return
  translationsStore.lesson = Number(value)
}

function statusIcon(status: TranslationStatus | null) {
  if (status === 'ok') return Check
  if (status === 'sample') return Flag
  return CircleHelp
}

function statusClass(status: TranslationStatus | null): string {
  if (status === 'ok') return 'text-emerald-500'
  if (status === 'sample') return 'text-blue-500'
  return 'text-muted-foreground'
}

function selectMaterial(materialId: number): void {
  translationsStore.materialId = materialId
}

function scrollToSelectedItem(): void {
  void nextTick(() => {
    const item = menuRef.value?.querySelector('[data-selected="true"]')
    item?.scrollIntoView({behavior: 'smooth', block: 'nearest'})
  })
}

function handleStatusUpdated(translateId: number, status: TranslationStatus): void {
  const item = translationsStore.translationStatuses.find((entry) => entry.translate_id === translateId)
  if (item) item.status = status
  if (translationsStore.translation?.translate_id === translateId) translationsStore.translation.status = status
}

async function performSearch(resetPage = false): Promise<void> {
  if (!searchParams.text.trim()) {
    searchResults.value = []
    totalSearchResults.value = 0
    return
  }
  if (resetPage) {
    currentPage.value = 1
    searchParams.skip = 0
  }
  isSearching.value = true
  try {
    const result = await cpApi.search.search({
      text: searchParams.text,
      target_lang: translationsStore.targetLang,
      native_lang: translationsStore.nativeLang,
      scope: searchParams.scope,
      skip: searchParams.skip,
      limit: searchParams.limit,
    })
    searchResults.value = result.items
    totalSearchResults.value = result.total
  } finally {
    isSearching.value = false
  }
}

function goToSearchPage(page: number): void {
  if (page < 1 || page > totalPages.value || page === currentPage.value) return
  currentPage.value = page
  searchParams.skip = (page - 1) * searchParams.limit
  void performSearch()
}

function selectSearchResult(materialId: number): void {
  const result = searchResults.value.find((item) => item.material_id === materialId)
  if (!result) return
  translationsStore.level = result.level
  translationsStore.unit = result.unit
  translationsStore.lesson = result.lesson
  activeTab.value = 'navigation'
  window.setTimeout(() => selectMaterial(materialId), 300)
}

function truncateText(text: string, searchText: string, maxLength = 80): string {
  if (!searchText.trim() || text.length <= maxLength) return text
  const index = text.toLowerCase().indexOf(searchText.toLowerCase())
  if (index < 0) return `${text.slice(0, maxLength)}...`
  const start = Math.max(0, index - Math.floor((maxLength - searchText.length) / 2))
  const end = Math.min(text.length, start + maxLength)
  return `${start > 0 ? '...' : ''}${text.slice(start, end)}${end < text.length ? '...' : ''}`
}

function highlightText(text: string, searchText: string): string {
  if (!searchText.trim()) return text
  const escaped = searchText.replace(/[.*+?^${}()|[\\]\\]/g, '\\$&')
  return text.replace(new RegExp(`(${escaped})`, 'gi'), '<mark>$1</mark>')
}

watch([() => translationsStore.targetLang, () => translationsStore.nativeLang], () => {
  void translationsStore.updateMaterialIndex()
})
watch([() => translationsStore.level, () => translationsStore.unit, () => translationsStore.lesson], () => {
  void translationsStore.getSlides()
})
watch(
  () => translationsStore.materialId,
  async (value) => {
    selectedMenuKeys.value = value ? [String(value)] : []
    await translationsStore.getMaterial()
    scrollToSelectedItem()
  }
)

onMounted(async () => {
  await translationsStore.updateMaterialIndex()
  await translationsStore.getSlides()
  await translationsStore.getMaterial()
  if (translationsStore.materialId) selectedMenuKeys.value = [String(translationsStore.materialId)]
  scrollToSelectedItem()
})
</script>

<template>
  <Teleport defer to="#app-header-left">
    <span class="text-sm text-muted-foreground">{{ $t('common.controlPanel') }}</span>
  </Teleport>
  <div class="space-y-6 p-6 pb-24">
    <div class="flex flex-wrap items-center gap-3">
      <Select :model-value="translationsStore.targetLang" @update:model-value="setTargetLang">
        <SelectTrigger class="w-[220px]"><SelectValue :placeholder="$t('profile.targetLanguage')" /></SelectTrigger>
        <SelectContent>
          <SelectItem v-for="lang in translationsStore.availableTargetLangs" :key="lang" :value="lang">{{
            $t(`common.${lang}`)
          }}</SelectItem>
        </SelectContent>
      </Select>
      <span class="text-sm text-muted-foreground">для</span>
      <Select :model-value="translationsStore.nativeLang" @update:model-value="setNativeLang">
        <SelectTrigger class="w-[220px]"><SelectValue :placeholder="$t('profile.nativeLanguage')" /></SelectTrigger>
        <SelectContent>
          <SelectItem v-for="lang in translationsStore.availableNativeLangs" :key="lang" :value="lang">{{
            $t(`profile.speakers.${lang}`)
          }}</SelectItem>
        </SelectContent>
      </Select>
    </div>

    <Accordion
      v-if="translationsStore.showMaterialSelectors"
      v-model="customInstructionsOpen"
      type="single"
      collapsible
      class="rounded-lg border px-4">
      <AccordionItem value="instructions" class="border-0">
        <AccordionTrigger>Custom Instructions</AccordionTrigger>
        <AccordionContent
          ><TranslationSettings :target-lang="translationsStore.targetLang" :native-lang="translationsStore.nativeLang"
        /></AccordionContent>
      </AccordionItem>
    </Accordion>

    <div class="grid gap-6 lg:grid-cols-[260px_minmax(0,1fr)]">
      <aside class="min-w-0">
        <Tabs v-model="activeTab" class="w-full">
          <TabsList class="grid w-full grid-cols-2">
            <TabsTrigger value="navigation">Navigation</TabsTrigger>
            <TabsTrigger value="search" data-testid="control-panel-tab-search">Search</TabsTrigger>
          </TabsList>
          <TabsContent value="navigation" class="space-y-3">
            <Select
              :model-value="translationsStore.level ? String(translationsStore.level) : SELECT_NONE"
              @update:model-value="setLevel">
              <SelectTrigger><SelectValue :placeholder="$t('common.level')" /></SelectTrigger>
              <SelectContent
                ><SelectItem v-for="level in translationsStore.levels" :key="level.level" :value="String(level.level)"
                  >{{ $t('common.level') }} {{ translationsStore.arabicToRomanNumber(level.level) }}</SelectItem
                ></SelectContent
              >
            </Select>
            <Select
              :model-value="translationsStore.unit ? String(translationsStore.unit) : SELECT_NONE"
              :disabled="!translationsStore.level"
              @update:model-value="setUnit">
              <SelectTrigger><SelectValue :placeholder="$t('common.unit')" /></SelectTrigger>
              <SelectContent
                ><SelectItem v-for="unit in translationsStore.filteredUnits" :key="unit.unit" :value="String(unit.unit)"
                  >{{ $t('common.unit') }} {{ unit.unit }}</SelectItem
                ></SelectContent
              >
            </Select>
            <Select
              :model-value="translationsStore.lesson ? String(translationsStore.lesson) : SELECT_NONE"
              :disabled="!translationsStore.unit"
              @update:model-value="setLesson">
              <SelectTrigger><SelectValue :placeholder="$t('common.lesson')" /></SelectTrigger>
              <SelectContent
                ><SelectItem
                  v-for="lesson in translationsStore.filteredLessons"
                  :key="lesson.lesson"
                  :value="String(lesson.lesson)"
                  >{{ $t('common.lesson') }} {{ lesson.lesson }}</SelectItem
                ></SelectContent
              >
            </Select>
            <div ref="menuRef" class="max-h-[500px] space-y-1 overflow-y-auto rounded-lg border p-1">
              <Button
                v-for="slide in translationsStore.slides"
                :key="slide.material_id"
                variant="ghost"
                class="h-auto min-h-10 w-full justify-start gap-2 whitespace-normal px-3 py-2 text-left"
                :data-selected="selectedMenuKeys.includes(String(slide.material_id))"
                :class="selectedMenuKeys.includes(String(slide.material_id)) ? 'bg-accent text-accent-foreground' : ''"
                @click="selectMaterial(slide.material_id)">
                <component
                  :is="statusIcon(translationsStore.getSlideStatus(slide.material_id))"
                  class="size-4 shrink-0"
                  :class="statusClass(translationsStore.getSlideStatus(slide.material_id))" />
                <span>{{ slide.slide }}. {{ slide.title }}</span>
              </Button>
              <p v-if="!translationsStore.slides.length" class="p-3 text-sm text-muted-foreground">
                {{ $t('common.select') }}
              </p>
            </div>
            <div class="grid gap-2 pt-2">
              <Button variant="outline" :disabled="!translationsStore.lesson" @click="translationsStore.goToNextCheck"
                ><CircleHelp class="mr-2 size-4" />Next to check</Button
              >
              <Button
                :disabled="!translationsStore.lesson || translationsStore.isBulkGenerating"
                @click="translationsStore.startBulkGeneration"
                >Generate All</Button
              >
            </div>
            <div v-if="translationsStore.isBulkGenerating" class="space-y-3 pt-2">
              <Button variant="destructive" class="w-full" @click="translationsStore.stopBulkGeneration">Stop</Button>
              <Progress :model-value="translationsStore.bulkGenerationProgress" />
            </div>
          </TabsContent>
          <TabsContent value="search" class="space-y-4">
            <form class="space-y-3" @submit.prevent="performSearch(true)">
              <div class="flex gap-2">
                <Input v-model="searchParams.text" placeholder="Enter search text" :disabled="isSearching" /><Button
                  type="submit"
                  :disabled="isSearching"
                  data-testid="control-panel-search"
                  aria-label="Search"
                  ><Search class="size-4"
                /></Button>
              </div>
              <RadioGroup v-model="searchParams.scope" class="flex gap-4">
                <div
                  v-for="scope in [
                    {value: 'material', label: 'Source'},
                    {value: 'translate', label: 'Transl.'},
                    {value: 'both', label: 'Both'},
                  ]"
                  :key="scope.value"
                  class="flex items-center gap-2">
                  <RadioGroupItem :id="`scope-${scope.value}`" :value="scope.value" /><Label
                    :for="`scope-${scope.value}`"
                    >{{ scope.label }}</Label
                  >
                </div>
              </RadioGroup>
            </form>
            <div v-if="searchResults.length" class="max-h-[500px] space-y-1 overflow-y-auto">
              <Button
                v-for="item in searchResults"
                :key="item.material_id"
                variant="ghost"
                class="h-auto w-full justify-start whitespace-normal px-2 py-2 text-left"
                @click="selectSearchResult(item.material_id)">
                <span
                  ><span class="block"
                    >{{ item.level }}.{{ item.unit }}.{{ item.lesson }}.{{ item.slide }} — {{ item.title }}</span
                  ><span
                    v-for="source in item.sources"
                    :key="source"
                    class="block text-xs text-muted-foreground"
                    v-html="highlightText(truncateText(source, searchParams.text), searchParams.text)" /><span
                    v-for="translation in item.translations"
                    :key="translation"
                    class="block text-xs text-muted-foreground"
                    v-html="highlightText(truncateText(translation, searchParams.text), searchParams.text)"
                /></span>
              </Button>
            </div>
            <nav
              v-if="totalPages > 1"
              aria-label="Search result pages"
              class="flex items-center justify-between gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                :disabled="currentPage === 1 || isSearching"
                @click="goToSearchPage(currentPage - 1)">
                Previous
              </Button>
              <span class="text-xs text-muted-foreground">{{ currentPage }} / {{ totalPages }}</span>
              <Button
                variant="outline"
                size="sm"
                :disabled="currentPage === totalPages || isSearching"
                data-testid="control-panel-search-next"
                @click="goToSearchPage(currentPage + 1)">
                Next
              </Button>
            </nav>
            <p v-if="!searchResults.length && searchParams.text && !isSearching" class="text-sm text-muted-foreground">
              No results found
            </p>
          </TabsContent>
        </Tabs>
      </aside>
      <main class="min-w-0">
        <TranslationMaterial
          v-if="translationsStore.material"
          :material="translationsStore.material"
          :translation="translationsStore.translation"
          :native-lang="translationsStore.nativeLang"
          :translation-statuses="translationsStore.translationStatuses"
          @go-to-next-check="translationsStore.goToNextCheck"
          @status-updated="handleStatusUpdated" />
      </main>
    </div>
  </div>
</template>
